import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  UserPlus, Search, Pencil, Trash2, KeyRound, Power, Loader2, Users, Lock, Unlock,
  Upload, Download, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", employee_id: "", mobile: "", email: "", region: "", username: "", password: "" };

const Field = ({ label, ...props }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-[0.12em] text-stone-500">{label}</label>
    <input
      {...props}
      className="mt-1.5 flex h-10 w-full rounded-md border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
    />
  </div>
);

const PAGE_SIZE = 50;

export default function MRManagement() {
  const [mrs, setMrs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(null); // {mode, data}
  const [pwdDialog, setPwdDialog] = useState(null); // mr
  const [newPwd, setNewPwd] = useState("");
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState("");
  const [deletingAll, setDeletingAll] = useState(false);

  const load = useCallback(async (q = "", p = 1, silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const params = { page: p, page_size: PAGE_SIZE };
      if (q) params.search = q;
      const { data } = await api.get("/mrs", { params });
      setMrs(data.items || []);
      setTotal(data.total ?? 0);
      setPage(data.page ?? p);
      setPages(data.pages ?? 1);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const silent = mrs.length > 0 || page > 1;
    const delay = search ? 350 : 0;
    const t = setTimeout(() => load(search, page, silent), delay);
    return () => clearTimeout(t);
  }, [search, page, load]);

  const openAdd = () => setDialog({ mode: "add", data: { ...EMPTY } });
  const openEdit = (mr) => setDialog({ mode: "edit", data: { ...mr, password: "" } });

  const save = async () => {
    const d = dialog.data;
    setSaving(true);
    try {
      if (dialog.mode === "add") {
        const empId = d.employee_id.trim();
        if (!empId) { toast.error("Employee ID is required"); setSaving(false); return; }
        await api.post("/mrs", { ...d, username: empId, password: empId });
        toast.success("MR added");
      } else {
        const { password, role, active, created_at, id, ...payload } = d;
        await api.put(`/mrs/${d.id}`, payload);
        toast.success("MR updated");
      }
      setDialog(null);
      load(search, page, true);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (mr) => {
    try {
      await api.post(`/mrs/${mr.id}/toggle-active`);
      load(search, page, true);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (mr) => {
    if (!window.confirm(`Delete ${mr.name}?`)) return;
    try {
      await api.delete(`/mrs/${mr.id}`);
      toast.success("MR deleted");
      load(search, page, true);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const unlock = async (mr) => {
    try {
      await api.post(`/mrs/${mr.id}/unlock`);
      toast.success(`${mr.name} unlocked for more registrations`);
      load(search, page, true);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const resetPwd = async () => {
    if (newPwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post(`/mrs/${pwdDialog.id}/reset-password`, { password: newPwd });
      toast.success("Password reset");
      setPwdDialog(null); setNewPwd("");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const upd = (k, v) => setDialog((p) => ({ ...p, data: { ...p.data, [k]: v } }));

  const downloadSample = async (format) => {
    try {
      const res = await api.get("/mrs/import-sample", {
        params: { format },
        responseType: "blob",
      });
      const ext = format === "xlsx" ? "xlsx" : "csv";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mr_import_sample.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErr(e, "Could not download sample"));
    }
  };

  const runBulkImport = async () => {
    if (!importFile) {
      toast.error("Choose a CSV or Excel file");
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/mrs/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`Imported ${data.created} of ${data.total} MR(s)`);
      if (data.created > 0) load(search, page, true);
    } catch (e) {
      toast.error(apiErr(e, "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  const closeImport = () => {
    setImportOpen(false);
    setImportFile(null);
    setImportResult(null);
  };

  const exportMrs = async (format) => {
    setExporting(format);
    try {
      const params = { format };
      if (search.trim()) params.search = search.trim();
      const res = await api.get("/mrs/export", { params, responseType: "blob" });
      const ext = format === "xlsx" ? "xlsx" : "csv";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mr_employees.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${total} employee(s) as ${ext.toUpperCase()}`);
    } catch (e) {
      toast.error(apiErr(e, "Export failed"));
    } finally {
      setExporting("");
    }
  };

  const deleteAllMrs = async () => {
    if (!total) {
      toast.error("No employees to delete");
      return;
    }
    const msg =
      `Delete ALL ${total} medical rep account(s) and every video they uploaded?\n\n` +
      "This cannot be undone. Templates and admin access are not affected.";
    if (!window.confirm(msg)) return;
    if (!window.confirm("Are you sure? Type OK in your mind — this permanently removes all MR data.")) return;

    setDeletingAll(true);
    try {
      const { data } = await api.delete("/mrs/all");
      toast.success(`Removed ${data.deleted_mrs} employee(s) and ${data.deleted_videos} video(s)`);
      load(search, page, true);
    } catch (e) {
      toast.error(apiErr(e, "Delete all failed"));
    } finally {
      setDeletingAll(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="mr-management">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Team</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-stone-900 mt-1">MR Management</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => exportMrs("csv")} disabled={!!exporting || loading}
            data-testid="export-mr-csv-button"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-60">
            {exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </button>
          <button type="button" onClick={() => exportMrs("xlsx")} disabled={!!exporting || loading}
            data-testid="export-mr-xlsx-button"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-60">
            {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            Export Excel
          </button>
          <button onClick={() => setImportOpen(true)} data-testid="bulk-import-mr-button"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-emerald-600 px-5 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50">
            <Upload className="h-4 w-4" /> Bulk Import
          </button>
          <button onClick={openAdd} data-testid="add-mr-button"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
            <UserPlus className="h-4 w-4" /> Add MR
          </button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
        <input
          data-testid="mr-search-input"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by name, email, employee ID…"
          className="h-11 w-full rounded-md border border-stone-300 bg-white pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-sm text-stone-500">
          {total > 0 ? (
            <>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} employees</>
          ) : (
            "No employees"
          )}
          {refreshing && <Loader2 className="inline h-3.5 w-3.5 ml-2 animate-spin text-emerald-600" />}
        </p>
        {pages > 1 && (
          <div className="flex items-center gap-2">
            <button type="button" disabled={page <= 1 || loading}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-md border border-stone-200 px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-40">
              Previous
            </button>
            <span className="text-sm text-stone-600">Page {page} of {pages}</span>
            <button type="button" disabled={page >= pages || loading}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-md border border-stone-200 px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-40">
              Next
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white">
        <table className="w-full">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200 text-left text-xs font-bold uppercase tracking-wider text-stone-500">
              <th className="px-6 py-4">Name</th>
              <th className="px-6 py-4">Employee ID</th>
              <th className="px-6 py-4">Contact</th>
              <th className="px-6 py-4">Region</th>
              <th className="px-6 py-4">Doctors</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-6 py-12 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600 mx-auto" /></td></tr>
            ) : mrs.length === 0 ? (
              <tr><td colSpan={7} className="px-6 py-12 text-center text-stone-500">
                <Users className="h-8 w-8 mx-auto mb-2 text-stone-300" /> No medical reps yet.
              </td></tr>
            ) : mrs.map((mr) => (
              <tr key={mr.id} data-testid={`mr-row-${mr.id}`} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                <td className="px-6 py-4">
                  <p className="font-medium text-stone-900">{mr.name}</p>
                  <p className="text-xs text-stone-400">@{mr.username}</p>
                </td>
                <td className="px-6 py-4 text-sm text-stone-600">{mr.employee_id}</td>
                <td className="px-6 py-4 text-sm text-stone-600">
                  <p>{mr.email}</p><p className="text-xs text-stone-400">{mr.mobile}</p>
                </td>
                <td className="px-6 py-4 text-sm text-stone-600">{mr.region}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span data-testid={`mr-usage-${mr.id}`} className="text-sm font-medium text-stone-700">{mr.doctor_count}/{mr.max_doctors}</span>
                    {mr.locked && <span data-testid={`mr-locked-${mr.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800"><Lock className="h-3 w-3" /> Locked</span>}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${mr.active ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-stone-600"}`}>
                    {mr.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-1">
                    {mr.locked && (
                      <button onClick={() => unlock(mr)} data-testid={`unlock-mr-${mr.id}`} title="Unlock (grant 20 more)" className="p-2 rounded-md text-amber-600 hover:bg-amber-50"><Unlock className="h-4 w-4" /></button>
                    )}
                    <button onClick={() => openEdit(mr)} data-testid={`edit-mr-${mr.id}`} title="Edit" className="p-2 rounded-md text-stone-500 hover:bg-stone-100 hover:text-stone-900"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => setPwdDialog(mr)} data-testid={`reset-pwd-${mr.id}`} title="Reset password" className="p-2 rounded-md text-stone-500 hover:bg-stone-100 hover:text-amber-600"><KeyRound className="h-4 w-4" /></button>
                    <button onClick={() => toggle(mr)} data-testid={`toggle-mr-${mr.id}`} title="Activate/Deactivate" className="p-2 rounded-md text-stone-500 hover:bg-stone-100 hover:text-emerald-600"><Power className="h-4 w-4" /></button>
                    <button onClick={() => del(mr)} data-testid={`delete-mr-${mr.id}`} title="Delete" className="p-2 rounded-md text-stone-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={deleteAllMrs}
            disabled={deletingAll || loading}
            data-testid="delete-all-mrs-button"
            className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-5 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
          >
            {deletingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Delete all employees
          </button>
        </div>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="mr-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{dialog?.mode === "add" ? "Add Medical Rep" : "Edit Medical Rep"}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
              <Field label="MR Name" data-testid="mr-field-name" value={dialog.data.name} onChange={(e) => upd("name", e.target.value)} />
              <Field label="Employee ID" data-testid="mr-field-empid" value={dialog.data.employee_id} onChange={(e) => upd("employee_id", e.target.value)} />
              <Field label="Mobile" data-testid="mr-field-mobile" value={dialog.data.mobile} onChange={(e) => upd("mobile", e.target.value)} />
              <Field label="Email" type="email" data-testid="mr-field-email" value={dialog.data.email} onChange={(e) => upd("email", e.target.value)} />
              <Field label="Region / Area" data-testid="mr-field-region" value={dialog.data.region} onChange={(e) => upd("region", e.target.value)} />
              {dialog.mode === "edit" && (
                <Field label="Username" data-testid="mr-field-username" value={dialog.data.username} onChange={(e) => upd("username", e.target.value)} />
              )}
              {dialog.mode === "add" && (
                <div className="sm:col-span-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                  The Employee ID will be used as both the username and the initial password for this MR.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setDialog(null)} className="rounded-md border border-stone-200 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="mr-save-button"
              className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk import dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => !o && closeImport()}>
        <DialogContent className="sm:max-w-lg" data-testid="bulk-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Bulk Import MRs</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-stone-600">
            <p>Upload a CSV or Excel file with columns: <strong>name, employee_id, mobile, email, region</strong>.</p>
            <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-3">
              Employee ID is used as both username and initial password — same as adding a single MR.
            </p>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => downloadSample("csv")}
                className="inline-flex items-center gap-2 rounded-md border border-stone-200 px-3 py-2 text-sm font-medium hover:bg-stone-50">
                <Download className="h-4 w-4" /> Sample CSV
              </button>
              <button type="button" onClick={() => downloadSample("xlsx")}
                className="inline-flex items-center gap-2 rounded-md border border-stone-200 px-3 py-2 text-sm font-medium hover:bg-stone-50">
                <FileSpreadsheet className="h-4 w-4" /> Sample Excel
              </button>
            </div>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              data-testid="bulk-import-file"
              onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
              className="block w-full text-sm text-stone-600 file:mr-3 file:rounded-md file:border-0 file:bg-emerald-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-emerald-700"
            />
            {importResult && (
              <div className="rounded-md border border-stone-200 bg-stone-50 p-3 max-h-48 overflow-y-auto">
                <p className="font-semibold text-stone-800 mb-2">
                  Created {importResult.created} · Failed {importResult.failed}
                </p>
                <ul className="space-y-1 text-xs">
                  {importResult.results?.filter((r) => r.status === "error").map((r) => (
                    <li key={`${r.row}-${r.employee_id}`} className="text-red-700">
                      Row {r.row} ({r.employee_id}): {r.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={closeImport} className="rounded-md border border-stone-200 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50">Close</button>
            <button onClick={runBulkImport} disabled={importing || !importFile} data-testid="bulk-import-submit"
              className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
              {importing && <Loader2 className="h-4 w-4 animate-spin" />} Import
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwdDialog} onOpenChange={(o) => { if (!o) { setPwdDialog(null); setNewPwd(""); } }}>
        <DialogContent className="sm:max-w-sm" data-testid="reset-pwd-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reset password</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-500">Set a new password for <strong>{pwdDialog?.name}</strong>.</p>
          <Field label="New password" type="text" data-testid="reset-pwd-input" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} />
          <DialogFooter>
            <button onClick={() => { setPwdDialog(null); setNewPwd(""); }} className="rounded-md border border-stone-200 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50">Cancel</button>
            <button onClick={resetPwd} disabled={saving} data-testid="reset-pwd-save"
              className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Reset
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
